/**
 * The permission-grants slice of the wire — Task T2-D (Tier-2 ops), consuming the
 * grants CRUD Task 15 already built and Task T2-A/T2-B's people directory reused for
 * subject resolution: `GET/POST /admin/permission-grants`,
 * `DELETE /admin/permission-grants/{id}` (`src/modules/permissions/routes.ts`,
 * `openapi.yaml`'s `PermissionGrantList` / `PermissionGrantRequest` / `PermissionGrant`
 * / `PermissionCode` schemas). Hand-written to mirror the contract, the same
 * discipline `employees.ts`'s own header states: transcribe what the screen reads,
 * not the whole document, and this task does not touch `openapi.yaml`.
 *
 * All three operations are `x-wp-permissions: [workforce.manage_policies]`,
 * `x-wp-scope: company` — the caller's active company, never a query parameter
 * (API_CONTRACT §7.4 rule 1). `GET` is unpaginated and company-wide: it returns
 * every grant, revoked and expired rows included, because "what has been granted
 * here" would be a lie if it hid a removal.
 *
 * ── `PERMISSION_CODES` is the 44-code enum, copied verbatim ─────────────────────
 *
 * `openapi.yaml`'s own `PermissionCode` schema description: "The 17 `workforce.*`
 * codes … plus the 27 `recruiting.*` / `hr.*` / `payroll.*` codes … — 44 in all." This
 * is a **fifth** copy of that vocabulary (after `src/lib/permissions.ts` +
 * `src/lib/hiring-permissions.ts` on the server, and `WORKFORCE_PERMISSIONS` /
 * `HIRING_PERMISSIONS` / `HR_PERMISSIONS` / `PAYROLL_PERMISSIONS` in `api/types.ts` on
 * this side), and deliberately not a re-export of the last one: `api/types.ts`'s
 * `HR_PERMISSIONS` is missing `hr.manage_leave_types` on purpose (its own header:
 * "gates a leave-type admin screen no task has built yet, so it is not spelled
 * here") because that list is a **menu spelling aid**. This screen is the admin
 * surface for granting *any* of the 44 codes, including the ones no menu item reads
 * yet — the whole reachability gap Task T2-D exists to close — so it needs the full
 * enum, not the narrower one. `permission-grants.test.ts` asserts this array equals
 * `openapi.yaml`'s own enum, read at test time, so a 45th code added there cannot
 * silently drift from this list.
 */

export const PERMISSION_GRANTS_PATH = '/api/v1/admin/permission-grants';

/** `DELETE /admin/permission-grants/{id}`. */
export function permissionGrantPath(id: string): string {
  return `${PERMISSION_GRANTS_PATH}/${encodeURIComponent(id)}`;
}

/** The 17 `workforce.*` codes, in `openapi.yaml`'s own declared order. */
const WORKFORCE_CODES = [
  'workforce.view_self',
  'workforce.view_department',
  'workforce.view_workspace',
  'workforce.manage_shifts',
  'workforce.manage_policies',
  'workforce.manage_devices',
  'workforce.view_apps',
  'workforce.view_websites',
  'workforce.view_file_events',
  'workforce.view_usb_events',
  'workforce.view_screenshots',
  'workforce.export_reports',
  'workforce.manage_alerts',
  'workforce.resolve_incidents',
  'workforce.view_audit_logs',
  'workforce.manage_retention',
  'workforce.use_ai_analytics',
] as const;

/** The 8 `recruiting.*` codes, in `openapi.yaml`'s own declared order. */
const RECRUITING_CODES = [
  'recruiting.view_jobs',
  'recruiting.manage_jobs',
  'recruiting.view_candidates',
  'recruiting.manage_candidates',
  'recruiting.view_candidate_documents',
  'recruiting.submit_feedback',
  'recruiting.manage_offers',
  'recruiting.hire',
] as const;

/** The 12 `hr.*` codes, in `openapi.yaml`'s own declared order — `manage_leave_types` included. */
const HR_CODES = [
  'hr.view_employee_hr',
  'hr.view_employee_documents',
  'hr.manage_employee_documents',
  'hr.manage_onboarding',
  'hr.view_leave',
  'hr.request_leave',
  'hr.approve_leave',
  'hr.manage_leave_types',
  'hr.view_reviews',
  'hr.manage_review_cycles',
  'hr.submit_review',
  'hr.view_own_review',
] as const;

/** The 7 `payroll.*` codes, in `openapi.yaml`'s own declared order. */
const PAYROLL_CODES = [
  'payroll.view_own_payslip',
  'payroll.view_payroll',
  'payroll.run_payroll',
  'payroll.approve_payroll',
  'payroll.ceo_approve',
  'payroll.manage_templates',
  'payroll.manage_regulations',
] as const;

/** All 44, in `openapi.yaml`'s own enum order — the four groups, group by group. */
export const PERMISSION_CODES = [
  ...WORKFORCE_CODES,
  ...RECRUITING_CODES,
  ...HR_CODES,
  ...PAYROLL_CODES,
] as const;

export type PermissionCode = (typeof PERMISSION_CODES)[number];

/** `PermissionGrantRequest.subject_type` / `PermissionGrant.subject_type`. */
export const PERMISSION_SUBJECT_TYPES = ['employee', 'role', 'department', 'job_title'] as const;
export type PermissionSubjectType = (typeof PERMISSION_SUBJECT_TYPES)[number];

/** `PermissionGrantRequest.effect` / `PermissionGrant.effect`. Server default is `allow`. */
export const PERMISSION_EFFECTS = ['allow', 'deny'] as const;
export type PermissionEffect = (typeof PERMISSION_EFFECTS)[number];

/**
 * `PermissionGrantRequest` of `openapi.yaml`. `subject_key` is the employee id for
 * `employee`, the role name for `role`, the achau department id for `department`, and
 * the free-text job title for `job_title` — the schema's own description, transcribed.
 */
export interface PermissionGrantRequest {
  readonly subject_type: PermissionSubjectType;
  readonly subject_key: string;
  readonly permission_code: PermissionCode;
  readonly effect?: PermissionEffect;
  readonly reason?: string;
  /** ISO-8601, must be in the future — `wp_perm_grants_expiry_ck`. */
  readonly expires_at?: string;
}

/**
 * `PermissionGrant` of `openapi.yaml` — one `wp_permission_grants` row. Every member
 * the schema marks `required` is written out here, `T | null` where it is nullable,
 * because `renderGrant` (`src/modules/permissions/service.ts`) always sends the key.
 */
export interface PermissionGrant {
  readonly id: string;
  readonly company_id: string;
  readonly subject_type: PermissionSubjectType;
  readonly subject_key: string;
  readonly employee_id: string | null;
  readonly permission_code: PermissionCode;
  readonly effect: PermissionEffect;
  readonly reason: string | null;
  readonly granted_by: string | null;
  readonly granted_at: string;
  readonly expires_at: string | null;
  readonly revoked_at: string | null;
}

/** `PermissionGrantList` — `{ grants }`, unpaginated, company-wide, revoked/expired included. */
export interface PermissionGrantList {
  readonly grants: readonly PermissionGrant[];
}
